import { CheckBox, DisabledByDefault } from '@mui/icons-material';
import {
  Box,
  Button,
  Link,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import React, { useCallback, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import LoadingMask from '../../../components/LoadingMask';
import { useFileFolderSelection } from '../../../components/Providers/FileFolderSelectionProvider';
import { selectFolders } from '../../../redux/reducers/folderReducer';
import { startDataExtraction } from '../../../services/dealGPT';
import { SemaFileCategory } from '../../../Types/enums';
import { CustomFile } from '../../../Types/file';
import { formatDate } from '../../../util/dateUtils';
import { getIconForFile } from '../../../util/fileUtils';
import { getFileExtension } from '../../../util/FolderPathUtil';

interface CategorizedFile extends CustomFile {
  category: SemaFileCategory;
}

interface ValidateSemaFilesStepProps {
  projectId: string;
  onClear: () => void;
  onProcess: () => void;
  refetchRun: () => void;
}

const fileNamePatterns = {
  pptx: /health\s*check/i,
  ipRisk: /IPRiskWorksheet/i,
  securityWarning: /SecurityWarningWorksheet/i,
};

const STATUS_ICON_PROPS = {
  valid: {
    title: 'Valid SEMA File',
    IconComponent: CheckBox,
    sx: { color: 'green', mr: 1 },
  },
  invalid: {
    title: 'Invalid Sema File',
    IconComponent: DisabledByDefault,
    sx: { color: '#D44333', mr: 1 },
  },
};

const categorizeFile = (file: CustomFile): SemaFileCategory => {
  const ext = getFileExtension(file.fileName).toLowerCase();
  const name = file.fileName;

  if (ext === 'pptx' && fileNamePatterns.pptx.test(name)) {
    return SemaFileCategory.PPTX;
  }

  if (ext === 'xlsx' && fileNamePatterns.ipRisk.test(name)) {
    return SemaFileCategory.IPRISK_EXCEL;
  }

  if (ext === 'xlsx' && fileNamePatterns.securityWarning.test(name)) {
    return SemaFileCategory.SECURITY_WARNING_EXCEL;
  }

  return SemaFileCategory.INVALID;
};

const validateFiles = (files: CategorizedFile[]): { isValid: boolean; errors: string[] } => {
  const categoryCount = {
    [SemaFileCategory.PPTX]: 0,
    [SemaFileCategory.IPRISK_EXCEL]: 0,
    [SemaFileCategory.SECURITY_WARNING_EXCEL]: 0,
  };

  files.forEach(({ category }) => {
    if (category !== SemaFileCategory.INVALID) {
      categoryCount[category]++;
    }
  });

  const errors: string[] = [];

  if (categoryCount[SemaFileCategory.PPTX] !== 1) {
    errors.push(
      categoryCount[SemaFileCategory.PPTX] === 0
        ? 'Missing "Health Check" presentation file.'
        : 'Only one "Health Check" presentation is allowed.'
    );
  }

  if (categoryCount[SemaFileCategory.IPRISK_EXCEL] !== 1) {
    errors.push(
      categoryCount[SemaFileCategory.IPRISK_EXCEL] === 0
        ? 'Missing "IPRiskWorksheet" Excel file.'
        : 'Only one "IPRiskWorksheet" Excel file is allowed.'
    );
  }

  if (categoryCount[SemaFileCategory.SECURITY_WARNING_EXCEL] !== 1) {
    errors.push(
      categoryCount[SemaFileCategory.SECURITY_WARNING_EXCEL] === 0
        ? 'Missing "SecurityWarningWorksheet" Excel file.'
        : 'Only one "SecurityWarningWorksheet" Excel file is allowed.'
    );
  }

  return {
    isValid:
      categoryCount[SemaFileCategory.PPTX] === 1 &&
      categoryCount[SemaFileCategory.IPRISK_EXCEL] === 1 &&
      categoryCount[SemaFileCategory.SECURITY_WARNING_EXCEL] === 1,
    errors,
  };
};

const getStatusIcon = (category: SemaFileCategory): JSX.Element => {
  const isInvalid = category === SemaFileCategory.INVALID;
  const { title, IconComponent, sx } = isInvalid
    ? STATUS_ICON_PROPS.invalid
    : STATUS_ICON_PROPS.valid;

  return (
    <Tooltip title={title}>
      <IconComponent sx={sx} />
    </Tooltip>
  );
};

const ValidateSemaFilesStep: React.FC<ValidateSemaFilesStepProps> = ({
  projectId,
  onClear,
  onProcess,
  refetchRun,
}) => {
  const navigate = useNavigate();
  const { currentFolder } = useSelector(selectFolders);
  const { selectedFiles } = useFileFolderSelection();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const categorizedFiles = useMemo((): CategorizedFile[] => {
    return selectedFiles.map((file) => ({
      ...file,
      category: categorizeFile(file),
    }));
  }, [selectedFiles]);

  const { isValid, errors } = useMemo(() => {
    return validateFiles(categorizedFiles);
  }, [categorizedFiles]);

  const navigateToIndexPage = useCallback(() => {
    navigate(`/folder/${currentFolder?.id}/files/index`);
  }, [navigate, currentFolder?.id]);

  const handleStartProcessing = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      await startDataExtraction(projectId, { files: selectedFiles });
      refetchRun();
      onProcess();
    } catch (err) {
      console.error(err);
      setError('Failed to start data extraction');
    } finally {
      setLoading(false);
    }
  }, [projectId, selectedFiles, refetchRun, onProcess]);

  if (loading) {
    return (
      <Box mt={6} display="flex" flexDirection="column" alignItems="center">
        <LoadingMask isLoading={true} />
        <Typography mt={6}>Processing Files & Data ...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box mt={6} display="flex" flexDirection="column" alignItems="center">
        <Typography color="error" fontSize={18} fontWeight="bold">
          {error}
        </Typography>
        <Button sx={{ mt: 2 }} variant="outlined" color="primary" onClick={onClear}>
          Back
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="flex-end" alignItems="center" mb={2}>
        <Button onClick={onClear} color="primary" sx={{ textTransform: 'none', fontWeight: 500 }}>
          Clear All
        </Button>
      </Box>
      <Table sx={{ border: '1px solid #ccc' }}>
        <TableHead>
          {['File/Folder Name', 'Last Updated', 'Modified By'].map((header) => (
            <TableCell key={header}>
              <Typography variant="subtitle1" fontWeight="bold">
                {header}
              </Typography>
            </TableCell>
          ))}
        </TableHead>
        <TableBody>
          {categorizedFiles.map((file) => (
            <TableRow
              key={file.id}
              sx={{
                height: 56,
                '&:hover': { backgroundColor: '#fafafa' },
              }}
            >
              <TableCell
                sx={{
                  maxWidth: 250,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                <Box display="flex" alignItems="center">
                  {getStatusIcon(file.category)}
                  {getIconForFile(file.fileName)}
                  <Typography component="span" sx={{ ml: 0.5 }}>
                    {file.fileName}
                  </Typography>
                </Box>
              </TableCell>
              <TableCell>
                <Typography fontWeight="bold">{formatDate(file.lastModified ?? '')}</Typography>
              </TableCell>
              <TableCell>
                <Typography fontWeight="500">{file.userId || '-'}</Typography>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      {errors.length > 0 && (
        <Box mt={2}>
          {errors.map((error, idx) => (
            <Typography key={idx} color="error">
              • {error}
            </Typography>
          ))}
          <Typography color="error">
            Update files in the Teams Channel folders to include the correct files if they are not
            already indexed.
            <Link onClick={navigateToIndexPage} sx={{ cursor: 'pointer' }} underline="hover">
              {' '}
              Re-indexing the project will be required.
            </Link>
          </Typography>
        </Box>
      )}
      <Box mt={3} display="flex" justifyContent="center">
        <Button
          variant="contained"
          color="success"
          disabled={!isValid}
          onClick={handleStartProcessing}
        >
          Start Processing
        </Button>
      </Box>
    </Box>
  );
};

export default ValidateSemaFilesStep;
